import React, { useContext, useCallback, useMemo, Fragment, useState, useEffect } from 'react';

import { CurrentPageContext, HistoryStackContext } from 'app/home/general-components/HistoryStackContext';
import { LocalizationContext, useLocalizedStrings } from 'app/localization/Context';
import { ChooseAvatar } from 'app/home/new-home/ChooseHomeType';
import { stringFormat } from 'app/model/BindData';
import Toggle, { ToggleHeadIconType } from 'app/home/general-components/Toggle/Toggle';
import Popup, { usePopup } from 'app/home/general-components/Popup/Popup';

import { isValidMobilePhoneNumber, markupMobilePhoneNumberWithCountryCode } from 'app/home/general-components/NumberInputHelper';
import { getHomeAvatar, LeaveHomePopup } from 'app/home/new-home/HomeList';
import CustomIconBack from 'app/home/home-components/CustomIconBack';

import { ReactComponent as IconFlagsIr } from 'images/flag/ir.svg';
import { ReactComponent as IconFlagsArIQ } from 'images/flag/ar-IQ.svg';
import { ReactComponent as IconFlagsUk } from 'images/flag/uk.svg';
import { ReactComponent as IconMe } from 'images/setting/me.svg';
import { ReactComponent as IconMeFa } from 'images/setting/me-fa.svg';
import { ReactComponent as IconMeAr } from 'images/setting/me-ar.svg';
import { ReactComponent as IconDelete } from 'images/general/delete-white-boxed.svg';
import { ReactComponent as IconTick } from 'images/general/tick-white-boxed.svg';
import { ReactComponent as IconBack } from 'images/general/back.svg';
import { ReactComponent as Avatar1 } from 'images/profile/avatar-1.svg';
import { ReactComponent as Avatar2 } from 'images/profile/avatar-2.svg';
import { ReactComponent as Avatar3 } from 'images/profile/avatar-3.svg';
import { ReactComponent as Avatar4 } from 'images/profile/avatar-4.svg';
import { ReactComponent as Avatar5 } from 'images/profile/avatar-5.svg';
import { ReactComponent as Avatar6 } from 'images/profile/avatar-6.svg';
import { ReactComponent as AvatarUnknown } from 'images/profile/avatar-unknown.svg';
import { ReactComponent as IconCamera } from 'images/profile/icon-camera.svg';
import { ReactComponent as IconEdit } from 'images/profile/edit.svg';
import { ReactComponent as IconDeleteWhite } from 'images/general/delete-white.svg';
import { ReactComponent as IconDeleteRed } from 'images/general/delete-red.svg';
import { ReactComponent as IconLanguage } from 'images/profile/language.svg';
import { ReactComponent as IconPlus } from 'images/general/icon-add.svg';
import { ReactComponent as IconAdd } from 'images/general/icon-add-black.svg';

import { ReactComponent as IconLoading } from 'images/general/clone-of-connecting-white.svg';
import { ReactComponent as IconSelection } from 'images/general/icon-badge-select.svg';

import { ReactComponent as IconBindNotif } from 'images/setting/bind-notif.svg';
import { ReactComponent as IconDevices } from 'images/homeTab/devices.svg';
import { ReactComponent as IconScenes } from 'images/homeTab/scenarios.svg';
import { ReactComponent as IconAutomations } from 'images/homeTab/binds.svg';
import { ReactComponent as IconSettings } from 'images/homeTab/settings.svg';


import './Users.scss'
import './EditProfile.scss'
import PhoneNumberInput from 'app/home/general-components/PhoneNumberInput/PhoneNumberInput';


export default function Users({ gateway, profile }) {
    const hs = useContext(HistoryStackContext);
    const p = useContext(CurrentPageContext);

    const l = useContext(LocalizationContext);
    const s = l.strings;
    const um = s.userManagement;
    const dismissHandler = p.dismiss;

    const currentUser = gateway.currentUser;

    const [joinedUsers, pendingUsers, currentUserId] = [gateway.allUsers.joinedUsers, gateway.allUsers.pendingUsers, gateway.currentUserId];

    const showPendingUserLoading = useMemo(() => {
        for (let i = 0; i < pendingUsers.length; i++) {
            const pu = pendingUsers[i];
            if (pu["loading"])
                return true;
        }
        return false;
    }, [pendingUsers]);
    const addUser = useCallback((user) => {
        if (!showPendingUserLoading)
            user.accept();
    }, [showPendingUserLoading]);
    const delUser = useCallback((user) => {
        if (!showPendingUserLoading)
            user.reject();
    }, [showPendingUserLoading]);

    const settingPermission = currentUser.permissions.setting;
    const isSolo = gateway.negotiatedProtocol.isSolo;

    return (
        <div className="user-management-page">
            <div className="bg-blank" />
            <div className="bg-secondary-color" />
            <div className="user-management-page-content">
                <div className="nav-button" onClick={dismissHandler} />
                <div className="header-group">
                    <CustomIconBack className="back-icon" />
                    <div />
                    {
                        (isSolo && settingPermission) ? (
                            <div className="button-border" onClick={() => hs.push("/settings/user_management/add")}>
                                <div className="label">{um.addUser}</div>
                                <IconPlus className="icon" />
                            </div>
                        ) : null
                    }
                </div>
                {/* {selectedUserForDetail === undefined ? <div className="nav-button" onClick={dismissHandler} /> : null} */}

                <div className="header">{um.title}</div>
                <div className="sub-header">{um.homeUsers}</div>
                <UserList
                    l={l} joinedUsers={joinedUsers} showNextIcon={true}
                    dataFetched={gateway.dataFetched} currentUserId={currentUserId}
                    onUserClick={(user) => hs.push("/settings/user_management/detail", { selectedUserId: user.user_id })}
                />

                {
                    pendingUsers.length === 0 ? null : <Fragment>
                        <div className="sub-header-with-loading">
                            <div className="text">
                                {um.pendingUsers}
                            </div>
                            {showPendingUserLoading ? <IconLoading className="loading" /> : null}
                        </div>
                        <div className="sub-header-info">{um.pendingUsersInfo}</div>
                        {
                            pendingUsers.map((u) => (
                                <div className={"pending-user " + (showPendingUserLoading ? "disable" : "")}
                                    key={"user-management-page-pending-user-" + String(u["user_id"])}>
                                    <UserAvatarIcon avatarId={u["avatar"]} className="avatar" />
                                    <div className="name">{u["name"]}</div>

                                    {
                                        currentUser.permissions.setting ? (
                                            <Fragment>
                                                <IconTick className="add-icon" onClick={() => addUser(u)} />
                                                <IconDelete className="delete-icon" onClick={() => delUser(u)} />
                                            </Fragment>
                                        ) : null
                                    }
                                </div>
                            ))
                        }
                    </Fragment>
                }
            </div>
        </div>
    );
}

export function UserList({
    l, joinedUsers, showNextIcon, dataFetched,
    currentUserId, onUserClick, selectedUserIds }) {
    const s = l.strings;
    const um = s.userManagement;

    const _selectedUserIds = selectedUserIds ? selectedUserIds : [];

    const sortedJoinedUsers = useMemo(() => {
        let a = [...joinedUsers];
        a.sort(function userOrder(u1, u2) {
            if (u1["user_id"] === currentUserId) {
                return -1;
            } else if (u2["user_id"] === currentUserId) {
                return 1;
            } else {
                return 0;
            }
        });
        return a;
    }, [joinedUsers, currentUserId]);

    return (
        <div className="user-list">
            {
                sortedJoinedUsers.length === 0 ? (
                    <div className="no-user">
                        {dataFetched ?
                            um.noUser : s.dataIsLoading}
                    </div>
                ) : (
                    sortedJoinedUsers.map((u) => (
                        <UserCell
                            key={"user-management-page-user-" + String(u["user_id"])}
                            l={l} user={u} isMe={u["user_id"] === currentUserId}
                            showNextIcon={showNextIcon} isSelected={_selectedUserIds.includes(u["user_id"])}
                            onUserClick={onUserClick} />
                    ))
                )
            }
        </div>
    )
}

export function shouldUserPNInsteadOfNameForUser(user) {
    return ((user["name"] === undefined) || (user["name"] == null) || (user["name"].length === 0)) &&
        (user["phone_number"] !== undefined) && (user["phone_number"] !== null)
}

function UserCell({ l, user, isMe, onUserClick, isSelected, showNextIcon }) {
    const meIcon = (l.language === 'en') ? IconMe : ((l.language === 'fa') ? IconMeFa : IconMeAr);

    const nameAdditional = <Fragment>
        {isMe ?
            React.createElement(meIcon, { className: "me-tag" }) : null}
        {isSelected ? " " : ""}
        {isSelected &&
            <IconSelection className="selection-tick" />}
    </Fragment>

    return (
        <div className={"user-cell" + (isSelected ? " selected" : "")} onClick={() => { onUserClick(user) }}>
            <UserAvatarIcon avatarId={user["avatar"]} className="avatar" />
            {(shouldUserPNInsteadOfNameForUser(user)) ?
                <div className="name empty">
                    {l.localizeNumber(markupMobilePhoneNumberWithCountryCode(user["phone_number"]))}{nameAdditional}
                </div>
                : <div className="name">{user["name"]}{nameAdditional}</div>}

            {showNextIcon ? <IconBack className="next-icon" /> : null}
        </div>
    )
}

export function UserDetailPage({ allUsers, currentUser, profile }) {
    const l = useContext(LocalizationContext);
    const s = l.strings;
    const um = s.userManagement;

    const joinedUsers = allUsers.joinedUsers;
    const updateProfileName = profile.updateProfileName;
    const updateProfileAvatar = profile.updateProfileAvatar;
    const sendChangeProfile = profile.sendChangeProfile;

    const p = useContext(CurrentPageContext);
    const dismiss = p.dismiss;
    const selectedUserId = p.state.selectedUserId;
    const isMe = selectedUserId === currentUser.user_id;
    const user = useMemo(() => {
        return joinedUsers.find(({ user_id }) => user_id === selectedUserId);
    }, [joinedUsers, selectedUserId]);

    // const user = useMemo(() => {
    //     if (user !== undefined) {
    //         const transformedUser = {...user};
    //         if ((transformedUser.name === null) || (transformedUser.name === undefined) || (transformedUser.name.length === 0)) {
    //             if ((transformedUser.phone_number !== null) && (transformedUser.phone_number !== undefined))
    //                 transformedUser.name = l.localizeAndFixSignedNumbers((transformedUser.phone_number));
    //         }
    //         console.log(transformedUser);
    //         return transformedUser;
    //     } else {
    //         return undefined;
    //     }
    // }, [user, l]);

    const [userCache, setUserCache] = useState(user);
    useEffect(() => {
        if (user !== undefined) {
            setUserCache(user);
        } else {
            dismiss();
        }
    }, [user, dismiss]);

    // edit avatar
    const [isChooseProfilePageActive, setChooseProfilePageActive] = useState(false);
    const updateAvatar = useCallback((avatar) => {
        updateProfileAvatar(avatar);
        sendChangeProfile({ "avatar": avatar });
    }, [updateProfileAvatar, sendChangeProfile]);
    const chooseAvatarDismiss = useCallback(() => {
        setChooseProfilePageActive(false);
    }, []);

    // edit name
    const [isChooseNameActive, _setChooseNameActive] = useState(false);
    const [tempName, setTempName] = useState("");
    const setChooseNameActive = useCallback(() => {
        setTempName(userCache.name);
        _setChooseNameActive(true);
    }, [userCache]);
    const updateName = useCallback(() => {
        if (tempName.length > 1) {
            updateProfileName(tempName);
            sendChangeProfile({ "name": tempName });
            _setChooseNameActive(false);
        }
    }, [updateProfileName, tempName, sendChangeProfile]);

    const [removeUserPopupId, showRemoveUserPopup, dismissRemoveUserPopup] = usePopup();
    const [languagePopupId, showLanguagePopup, dismissLanguagePopup] = usePopup();

    const setLang = useCallback((lang) => {
        dismissLanguagePopup();
        sendChangeProfile({ "language": lang });
    }, [sendChangeProfile, dismissLanguagePopup]);

    const [adminPopupFunction, setAdminPopupFunction] = useState(undefined);
    const [adminPermissionPopupId, showAdminPermissionPopup, dismissAdminPermissionPopup] = usePopup();
    const confirmAdminPopup = useCallback(() => {
        dismissAdminPermissionPopup();
        adminPopupFunction.func();
        setAdminPopupFunction(undefined);
    }, [adminPopupFunction, dismissAdminPermissionPopup])
    const isThereAnotherAdmin = useMemo(() => {
        for (let i = 0; i < joinedUsers.length; i++) {
            const u = joinedUsers[i];
            if ((u.permissions.setting) && (u["user_id"] !== currentUser["user_id"])) {
                return true;
            }
        }
        return false;
    }, [joinedUsers, currentUser]);

    const settingPermission = currentUser.permissions.setting;

    const checkAdminPermissionWrapper = useCallback((f) => {
        return () => {
            // console.log(settingPermission, isMeCache, f);
            if (settingPermission && isMe) {
                setAdminPopupFunction({ func: f });
                showAdminPermissionPopup();
            } else {
                f();
            }
        }
    }, [settingPermission, isMe, showAdminPermissionPopup]);

    let nameForHeader = userCache.name;
    let nameForNameSection = userCache.name;
    if ((userCache.name === null) || (userCache.name === undefined) || (userCache.name.length === 0)) {
        if ((userCache.phone_number !== null) && (userCache.phone_number !== undefined)) {
            nameForHeader = l.localizeAndFixSignedNumbers(userCache.phone_number);
            nameForNameSection = um.noName;
        }
    }

    return (
        <div className="user-detail-page">
            <div className="bg-blank" />
            <div className="bg-secondary-color" />
            <div className="user-detail-page-content">
                {!isChooseProfilePageActive ? <div className="nav-button" onClick={dismiss} /> : null}

                <div className="header-group">
                    <CustomIconBack className="back-icon" />
                    <div />
                    {
                        settingPermission ? (
                            <div className="button-border" onClick={showRemoveUserPopup}>
                                <div className="label">{um.remove}</div>
                                <IconDeleteWhite className="icon" />
                            </div>
                        ) : null
                    }
                </div>

                <div className="header">{stringFormat(um.detailTitleFormat, nameForHeader)}</div>

                <UserAvatarIcon avatarId={userCache.avatar} className="icon" />
                {isMe ? <Fragment>
                    <div className="border">
                        <div className="label">{s.changeAvatar}</div>
                        <IconCamera className="icon" />
                    </div>
                    <div className="avatar-click-panel" onClick={() => { setChooseProfilePageActive(true) }} />
                </Fragment> : null}

                {/* NAME */}
                <div className="input-label">{s.name}</div>
                {/* NAME: EDIT */}
                {isChooseNameActive ? (
                    <div className="input-box-container">
                        <input className="input-box" placeholder={userCache.name}
                            value={tempName} onChange={e => setTempName(e.target.value)}></input>
                        <div className="button" onClick={updateName}>{s.save}</div>
                    </div>
                ) : (
                    <div className="show-name-container">
                        <div className="label">
                            {nameForNameSection}
                        </div>
                        <div />
                        {isMe ? (
                            <div className="border" onClick={setChooseNameActive}>
                                <div className="label" onClick={setChooseNameActive}>{s.edit}</div>
                                <IconEdit className="icon" onClick={setChooseNameActive} />
                            </div>) : null}
                    </div>
                )}

                {/* PHONE NUMBER */}
                {
                    ((userCache.phone_number !== undefined) && (userCache.phone_number !== null)) ?
                        <Fragment>
                            <div className="input-label">{s.phoneNumber}</div>
                            <div className="show-name-container">
                                <div className="label" dir='ltr'>
                                    {l.localizeNumber(markupMobilePhoneNumberWithCountryCode(userCache.phone_number))}
                                </div>
                            </div>
                        </Fragment> : null
                }

                {
                    (userCache.language !== undefined) ? (
                        <Fragment>
                            {/* LANG */}
                            <div className="input-label">{s.language}</div>
                            {/* LANG: value */}
                            <div className="show-name-container">
                                {userCache.language === "en" ? (
                                    <div className="lang-label">
                                        <div className="text">
                                            English
                                        </div>
                                        <IconFlagsUk className="icon" />
                                    </div>
                                ) : (userCache.language === "fa" ? (
                                    <div className="lang-label">
                                        <div className="text">
                                            فارسی
                                        </div>
                                        <IconFlagsIr className="icon" />
                                    </div>
                                ) : (
                                    <div className="lang-label">
                                        <div className="text">
                                            العربية العراقية
                                        </div>
                                        <IconFlagsArIQ className="icon" />
                                    </div>
                                ))}
                                <div />
                                {isMe ? (
                                    <div className="border" onClick={showLanguagePopup}>
                                        <div className="label">{s.change}</div>
                                        <IconLanguage className="icon" />
                                    </div>) : null}
                            </div>
                        </Fragment>
                    ) : null
                }

                <div className="section notif">
                    <div className="title">{um.notif.title}</div>
                    {/* {userCache.notification.loading ? <IconLoading2 className="loading" /> : null} */}
                    <SimpleCard
                        isOn={userCache.notification.bind === 1}
                        onClick={() => { userCache.changeNotif((userCache.notification.bind === 1) ? 0 : 1) }}
                        unclickable={
                            (!settingPermission) ||
                            (userCache.notification.bind === 2)}
                        headType={
                            (userCache.notification.bind === 2) ?
                                ToggleHeadIconType.cloudOff : (
                                    settingPermission ?
                                        ToggleHeadIconType.checkOrX : ToggleHeadIconType.lock
                                )}
                        icon={IconBindNotif}
                        title={um.notif.automations} />
                </div>

                <div className="section permissions">
                    <div className="title">{um.permissions.title}</div>
                    {/* {userCache.permissions.loading ? <IconLoading3 className="loading" /> : null} */}
                    <SimpleCard
                        checkAdminPermissionWrapper={checkAdminPermissionWrapper}
                        isOn={userCache.permissions.setting}
                        onClick={() => { userCache.changePermissions({ setting: !userCache.permissions.setting }) }}
                        unclickable={!settingPermission}

                        icon={IconSettings}
                        title={um.permissions.admin}
                        headType={settingPermission ? ToggleHeadIconType.checkOrX : ToggleHeadIconType.lock} />

                    <SimpleCard
                        checkAdminPermissionWrapper={checkAdminPermissionWrapper}
                        isOn={userCache.permissions.device.change_state}
                        onClick={() => { userCache.changePermissions({ deviceCS: !userCache.permissions.device.change_state }) }}
                        unclickable={!settingPermission}

                        icon={IconDevices}
                        title={um.permissions.deviceChangeState}
                        headType={settingPermission ? ToggleHeadIconType.checkOrX : ToggleHeadIconType.lock} />

                    <SimpleCard
                        checkAdminPermissionWrapper={checkAdminPermissionWrapper}
                        isOn={userCache.permissions.bind.setting}
                        onClick={() => { userCache.changePermissions({ bindS: !userCache.permissions.bind.setting }) }}
                        unclickable={!settingPermission}

                        icon={IconAutomations}
                        title={um.permissions.automationSettings}
                        headType={settingPermission ? ToggleHeadIconType.checkOrX : ToggleHeadIconType.lock} />

                    <SimpleCard
                        checkAdminPermissionWrapper={checkAdminPermissionWrapper}
                        isOn={userCache.permissions.bind.change_enable}
                        onClick={() => { userCache.changePermissions({ bindE: !userCache.permissions.bind.change_enable }) }}
                        unclickable={!settingPermission}

                        icon={IconAutomations}
                        title={um.permissions.automationEnable}
                        headType={settingPermission ? ToggleHeadIconType.checkOrX : ToggleHeadIconType.lock} />

                    <SimpleCard
                        checkAdminPermissionWrapper={checkAdminPermissionWrapper}
                        isOn={userCache.permissions.scenario.setting}
                        onClick={() => { userCache.changePermissions({ scenarioS: !userCache.permissions.scenario.setting }) }}
                        unclickable={!settingPermission}

                        icon={IconScenes}
                        title={um.permissions.sceneSettings}
                        headType={settingPermission ? ToggleHeadIconType.checkOrX : ToggleHeadIconType.lock} />

                    <SimpleCard
                        checkAdminPermissionWrapper={checkAdminPermissionWrapper}
                        isOn={userCache.permissions.scenario.run}
                        onClick={() => { userCache.changePermissions({ scenarioR: !userCache.permissions.scenario.run }) }}
                        unclickable={!settingPermission}

                        icon={IconScenes}
                        title={um.permissions.sceneRun}
                        headType={settingPermission ? ToggleHeadIconType.checkOrX : ToggleHeadIconType.lock} />
                </div>

                <div style={{ height: 16 }} />

                <Popup popupId={removeUserPopupId} name="remove_user_popup" baseZIndex={12}>
                    <div className="user-remove-popup">
                        <div className="header">{s.userManagement.removeAreYouSure}</div>
                        <div className="buttons-group">
                            <div className="delete-button" onClick={() => {
                                dismissRemoveUserPopup(); setTimeout(() => {
                                    userCache.remove();
                                });
                            }}>
                                <div className="label">{s.userManagement.removeConfirmOK}</div>
                                <IconDeleteRed className="icon" />
                            </div>
                            <div className="dismiss-button" onClick={dismissRemoveUserPopup}>{s.userManagement.removeConfirmNO}</div>
                        </div>
                    </div>
                </Popup>

                <Popup popupId={adminPermissionPopupId} name="remove_permission_popup" baseZIndex={12}>
                    {isThereAnotherAdmin ?
                        <div className="general-popup">
                            <div className="header">{s.userManagement.adminAreYouSure}</div>
                            <div className="button red c1-4 with-icon" onClick={confirmAdminPopup}>
                                <div className="label">{s.userManagement.adminConfirmRemove}</div>
                                <IconDeleteRed className="icon" />
                            </div>
                            <div className="button black c4-7" onClick={dismissAdminPermissionPopup}>{s.userManagement.adminConfirmKeep}</div>
                        </div> :
                        <div className="general-popup">
                            <div className="header">{s.userManagement.adminCantRemoveAccessFromYourselfPopup.header}</div>
                            <div className="body with-more-margin">{s.userManagement.adminCantRemoveAccessFromYourselfPopup.body}</div>
                            <div className="button light full" onClick={dismissAdminPermissionPopup}>{s.userManagement.adminCantRemoveAccessFromYourselfPopup.ok}</div>
                        </div>}
                </Popup>

                <Popup inBottom popupId={languagePopupId} name="changeـlang_popup" baseZIndex={12}>
                    <div className="change-lang-popup">
                        <div className="header">{s.changeLanguage}</div>
                        <div className="lang-box en" onClick={() => setLang("en")}>
                            <IconFlagsUk className="icon" />
                            <div className="text">
                                English
                            </div>
                        </div>
                        <div className="lang-box ar" onClick={() => setLang("ar-IQ")}>
                            <IconFlagsArIQ className="icon" />
                            <div className="text">
                                العربية العراقية
                            </div>
                        </div>
                        <div className="lang-box fa" onClick={() => setLang("fa")}>
                            <IconFlagsIr className="icon" />
                            <div className="text">
                                فارسی
                            </div>
                        </div>
                    </div>
                </Popup>

                <ChooseAvatar bgClassName={"bg-secondary-color"}
                    isActive={isChooseProfilePageActive}
                    dismiss={chooseAvatarDismiss}
                    setAvatar={updateAvatar} />
            </div>

        </div>
    );
}

// for now only for solo
export function HomeDetailPage({
    home,
    currentUser, sendChangeProfile }) {
    const l = useContext(LocalizationContext);
    const s = l.strings;
    const um = s.userManagement;

    const p = useContext(CurrentPageContext);
    const dismiss = p.dismiss;

    const [homeCache, sethomeCache] = useState(home);
    useEffect(() => {
        if (home !== undefined) {
            sethomeCache(home);
        } else {
            dismiss();
        }
    }, [home, dismiss]);

    // edit avatar
    const [isChooseProfilePageActive, setChooseProfilePageActive] = useState(false);
    const updateAvatar = useCallback((avatar) => {
        sendChangeProfile({ "avatar": avatar });
    }, [sendChangeProfile]);
    const chooseAvatarDismiss = useCallback(() => {
        setChooseProfilePageActive(false);
    }, []);

    // edit name
    const [isChooseNameActive, _setChooseNameActive] = useState(false);
    const [tempName, setTempName] = useState("");
    const setChooseNameActive = useCallback(() => {
        setTempName(homeCache.name);
        _setChooseNameActive(true);
    }, [homeCache]);
    const updateName = useCallback(() => {
        if (tempName.length > 1) {
            sendChangeProfile({ "name": tempName });
            _setChooseNameActive(false);
        }
    }, [tempName, sendChangeProfile]);

    const [exitingHome, setExitingHome] = useState(undefined);

    const settingPermission = currentUser.permissions.setting;

    const [exitHomePopupId, showExitHomePopup, dismissExitHomePopup] = usePopup();

    const onExitHomeConfirmed = useCallback((leaveAndDelete = false) => {
        dismissExitHomePopup();
        setTimeout(() => {
            exitingHome.remove(leaveAndDelete);
            window.location.reload();
        });
    }, [dismissExitHomePopup, exitingHome]);

    return (
        <div className="user-detail-page">
            <div className="bg-blank" />
            <div className="bg-secondary-color" />
            <div className="user-detail-page-content">
                <div className="nav-button" onClick={dismiss} />

                <div className="header-group">
                    <CustomIconBack className="back-icon" />
                    <div />
                    {settingPermission ? <div className="button-border" onClick={() => { setExitingHome(home); showExitHomePopup(); }}>
                        <div className="label">{um.remove}</div>
                        <IconDeleteWhite className="icon" />
                    </div> : null}
                </div>

                {/* <div className="header">{stringFormat(um.detailTitleFormat, homeCache.name)}</div> */}
                <div className="header">{s.homeProfile}</div>

                {React.createElement(getHomeAvatar(homeCache.avatar, false), { className: "icon" })}
                {settingPermission ?
                    <Fragment>
                        <div className="border">
                            <div className="label">{s.changeAvatar}</div>
                            <IconCamera className="icon" />
                        </div>
                        <div className="avatar-click-panel" onClick={() => { setChooseProfilePageActive(true) }} />
                    </Fragment> : null}


                {/* NAME */}
                <div className="input-label">{s.name}</div>
                {/* NAME: EDIT */}
                {isChooseNameActive ? (
                    <div className="input-box-container">
                        <input className="input-box" placeholder={homeCache.name}
                            value={tempName} onChange={e => setTempName(e.target.value)}></input>
                        <div className="button" onClick={updateName}>{s.save}</div>
                    </div>
                ) : (
                    <div className="show-name-container">
                        <div className="label">
                            {homeCache.name}
                        </div>
                        <div />
                        {settingPermission ? (
                            <div className="border" onClick={setChooseNameActive}>
                                <div className="label" onClick={setChooseNameActive}>{s.edit}</div>
                                <IconEdit className="icon" onClick={setChooseNameActive} />
                            </div>) : null}
                    </div>
                )}

                <LeaveHomePopup
                    exitHomePopupId={exitHomePopupId}
                    exitingHome={exitingHome}
                    dismissExitHomePopup={dismissExitHomePopup}
                    onExitHomeConfirmed={onExitHomeConfirmed} />

                <ChooseAvatar forHome={true}
                    bgClassName={"bg-secondary-color"}
                    isActive={isChooseProfilePageActive}
                    dismiss={chooseAvatarDismiss}
                    setAvatar={updateAvatar} />
            </div>

        </div>
    );
}

function SimpleCard({ icon, title, isOn, onClick, unclickable, headType, checkAdminPermissionWrapper }) {
    let _onClick = onClick;
    if ((isOn) && (checkAdminPermissionWrapper !== undefined)) {
        _onClick = checkAdminPermissionWrapper(onClick);
    }
    return (
        <div className="simple-card">
            {React.createElement(icon, { className: "icon" })}
            <div className="title">{title}</div>
            <Toggle onClick={_onClick} enable={isOn} unclickable={unclickable} customHeadIconType={headType} />
        </div>
    )
}

// function SingleCard({ icon, title, isOn, onClick, infoOn, infoOff, unclickable }) {
//     return (
//         <div className="single-card">
//             {React.createElement(icon, { className: "icon" })}
//             <div className="title">{title}</div>
//             <div className="info">{isOn ? infoOn : infoOff}</div>
//             <Toggle onClick={onClick} enable={isOn} unclickable={unclickable} />
//         </div>
//     )
// }

// function TwinsCard({
//     icon, title,
//     isOn1, isOn2, onClick1, onClick2,
//     infoOn1, infoOff1,
//     infoOn2, infoOff2,
//     unclickable1, unclickable2 }) {
//     return (
//         <div className="twins-card">
//             {React.createElement(icon, { className: "icon" })}
//             <div className="title">{title}</div>

//             <div className="info">{isOn1 ? infoOn1 : infoOff1}</div>
//             <Toggle onClick={onClick1} enable={isOn1} unclickable={unclickable1} />

//             <div className="info">{isOn2 ? infoOn2 : infoOff2}</div>
//             <Toggle onClick={onClick2} enable={isOn2} unclickable={unclickable2} />
//         </div>
//     )
// }

export function UserAvatarIcon({ avatarId, className }) {
    switch (avatarId) {
        case 1:
            return <Avatar1 className={className} />;
        case 2:
            return <Avatar2 className={className} />;
        case 3:
            return <Avatar3 className={className} />;
        case 4:
            return <Avatar4 className={className} />;
        case 5:
            return <Avatar5 className={className} />;
        case 6:
            return <Avatar6 className={className} />;
        case 0:
        default:
            return <AvatarUnknown className={className} />;
    }
}

export function GetNumberToAddUser({ addUser }) {
    const s = useLocalizedStrings();
    const um = s.userManagement;

    const p = useContext(CurrentPageContext);
    const dismiss = p.dismiss;

    const [_countryCode, _setCountryCode] = useState('');
    const [_phoneNumber, _setPhoneNumber] = useState('');

    const onPhoneNumberChange = useCallback((countryCode, phoneNumber) => {
        _setCountryCode(countryCode);
        _setPhoneNumber(phoneNumber);
    }, []);


    const doneClick = useCallback(() => {
        if (isValidMobilePhoneNumber(_countryCode, _phoneNumber)) {
            const num = "+" + _countryCode + _phoneNumber;
            addUser(num);
            dismiss();
        }
    }, [addUser, dismiss, _countryCode, _phoneNumber]);

    return (
        <div className="add-user-page">
            <div className="bg-blank" />
            <div className="bg-secondary-color" />
            <div className="add-user-page-content">
                <CustomIconBack className="nav-icon" />
                <div className="nav-button" onClick={dismiss} />

                <div className="page-header">{um.addUser}</div>
                <div className="page-header-info" style={{ marginTop: 8 }}>{um.addUserInfo}</div>

                <PhoneNumberInput
                    style={{ marginTop: 24 }}
                    onPhoneNumberChange={onPhoneNumberChange}
                />
                <div />
                {!isValidMobilePhoneNumber(_countryCode, _phoneNumber) ? <div className="error-label">{s.youMustEnterAPN}</div> : null}
                <div className="full-blue-button round-button" onClick={doneClick} style={{ opacity: isValidMobilePhoneNumber(_countryCode, _phoneNumber) ? 1 : 0.3 }}>
                    <div>{um.addUser}</div> <IconAdd className="icon" />
                </div>
            </div>
        </div>
    )
}